import { memo, useCallback, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { bloxValues } from 'themes/constants';
import { t } from 'i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import { getSolidThemeGray } from 'utility/styles';
import { SxProps, Theme, useTheme } from '@mui/material/styles';

/**
 * Access Restricted - Card or Full View
 * --------------------------------------------------------
 * Generic component to display if a user does not
 * have permission to a specific view, eg create assets.
 *
 * @example
 *   <NoAccess
 *      permissionName={'read assets'}
 *      actions={false}
 *      mode="full"
 *    />
 *
 */

export interface NoAccessContentProps {
  header?: string;
  details?: string;
  contact?: string;
  // set false to hide actions (eg back buton)
  actions?: boolean;
  mode?: 'full' | 'card';
  // shorthand for details msg, can pass only permName (no details)
  // and will automatically pass string to translation
  permName?: string;
  // Mui color variants for severity levels.
  severity?: 'error' | 'warning' | 'info' | 'success';
}

export interface NoAccessProps extends NoAccessContentProps {
  // inherits all props from NoAccessContentProps
}

/**
 * NoAccessContent - Inner content without background wrapper
 * --------------------------------------------------------
 * The inner content box that can be used independently
 * without the background wrapper. Useful when you want
 * to embed the messaging in a custom layout.
 */
export const NoAccessContent = memo(
  ({
    header,
    details,
    contact,
    actions = true,
    mode = 'card',
    permName,
    severity = 'warning',
  }: NoAccessContentProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { lastLocation } = useCosmosRuntime();
    const { activeSite } = useCosmos();
    const isDarkMode = theme.palette.mode === 'dark';

    const navBackPath = useMemo(() => {
      return lastLocation
        ? `${lastLocation?.pathname}${lastLocation?.search}`
        : `/${activeSite}/`;
    }, [lastLocation, activeSite]);

    const buttonBackHandler = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        navigate(navBackPath);
      },
      [navigate, navBackPath],
    );

    const headerMsg = header ?? t('blox.permissions.msg.access_restricted');
    const contactMsg = contact ?? t('blox.permissions.msg.contact_admin');

    const detailsMsg = details
      ? details
      : permName
        ? t('blox.permissions.msg.required_long', { name: permName })
        : undefined;

    const innerBoxSx: SxProps<Theme> = useMemo(
      () => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        bgcolor: theme.palette.background.paper,
        width: mode === 'card' ? '90%' : '100%',
        maxWidth: mode === 'card' ? 500 : '100%',
        boxShadow: mode === 'card' ? theme.shadows[2] : 'none',
        borderRadius: mode === 'card' ? 2 : 0,
        border: mode === 'card' ? `1px solid ${theme.palette.divider}` : 'none',
        overflow: mode === 'card' ? 'hidden' : 'auto',
      }),
      [theme, mode],
    );

    const actionsBoxSx: SxProps<Theme> = useMemo(
      () => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        p: 2,
        borderBottomLeftRadius: 2,
        borderBottomRightRadius: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: mode === 'card' ? getSolidThemeGray(theme, true) : 'inherit',
      }),
      [theme, mode],
    );

    return (
      <Box
        id="no-access-inner"
        data-testid="no-access-inner"
        sx={innerBoxSx}
      >
        <Box
          id="no-access-header"
          data-testid="no-access-header"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            gap: 2,
            p: 2,
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: isDarkMode
              ? theme.palette[severity].dark
              : theme.palette[severity].light,
          }}
        >
          <DangerousOutlinedIcon
            sx={{
              fontSize: bloxValues.iconSizeLarge,
              color: theme.palette.text.primary,
              borderRadius: '8px',
              p: '2px',
              bgcolor: theme.palette.action.hover,
              border: `1px solid ${theme.palette.action.selected}`,
            }}
          />

          {headerMsg && (
            <Typography
              id="no-access-reason"
              data-testid="no-access-reason"
              sx={{
                fontSize: bloxValues.xxlFontSizeRem,
              }}
            >
              {headerMsg}
            </Typography>
          )}
        </Box>

        <Box
          id="no-access-info"
          data-testid="no-access-info"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            bgcolor: theme.palette.background.paper,
            gap: 2,
            px: 2,
            py: 3,
          }}
        >
          {detailsMsg && (
            <Typography
              id="no-access-details"
              data-testid="no-access-details"
              sx={{
                fontSize: bloxValues.xlFontSizeRem,
                fontWeight: bloxValues.fontWeightMedium,
                color: theme.palette.text.primary,
              }}
            >
              {detailsMsg}
            </Typography>
          )}

          {contactMsg && (
            <Typography
              id="no-access-contact"
              data-testid="no-access-contact"
              sx={{
                fontSize: bloxValues.smallFontSizeRem,
                color: theme.palette.text.secondary,
              }}
            >
              {contactMsg}
            </Typography>
          )}
        </Box>

        {actions && (
          <Box
            id="no-access-actions"
            data-testid="no-access-actions"
            sx={actionsBoxSx}
          >
            <Button
              id="no-access-back-button"
              data-testid="no-access-back-button"
              variant="contained"
              size="large"
              disableElevation={true}
              href={navBackPath}
              startIcon={<ArrowBackIcon sx={{ fontSize: bloxValues.iconSizeRegular }} />}
              onClick={buttonBackHandler}
            >
              {t('blox.actions.back')}
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

NoAccessContent.displayName = 'NoAccessContent';

/**
 * NoAccess - Page Filler with background wrapper
 * --------------------------------------------------------
 * Wraps NoAccessContent with a background container.
 * Use this when you want the full card/page layout.
 */
export const NoAccess = memo(
  ({
    header,
    details,
    contact,
    actions = true,
    mode = 'card',
    permName,
    severity = 'warning',
  }: NoAccessProps) => {
    const theme = useTheme();

    const parentBoxSx: SxProps<Theme> = useMemo(
      () => ({
        display: 'flex',
        alignItems: mode === 'card' ? 'center' : 'flex-start',
        justifyContent: mode === 'card' ? 'center' : 'flex-start',
        bgcolor:
          mode === 'card'
            ? theme.palette.action.selected
            : theme.palette.background.paper,
        height: '100%',
        width: '100%',
      }),
      [theme, mode],
    );

    return (
      <Box
        id="no-access-card"
        data-testid="no-access-card"
        sx={parentBoxSx}
      >
        <NoAccessContent
          header={header}
          details={details}
          contact={contact}
          actions={actions}
          mode={mode}
          permName={permName}
          severity={severity}
        />
      </Box>
    );
  },
);

NoAccess.displayName = 'NoAccess';
